/**
 * Cancellation-save flow — the dialog behind "Cancel subscription".
 *
 * Step 1 asks why (six categories, with inline follow-up choices).
 * The selected specific reason branches to a save offer:
 *   too_expensive / other / switched_provider → 25% off the next renewal
 *   order issues           → apology + the same 25% offer
 *   not_working            → results-take-time note + the same 25% offer
 *   enough_supply / taking_break / reached_goal → pause instead (1 / 2 months)
 *   side_effects / dislike_treatment → cancellation confirmation
 * "No thanks, continue to cancel" is always one tap away — the flow
 * saves patients with information and offers, never with friction.
 *
 * Every entry is recorded server-side in
 * subscription_cancellation_attempts (reason, offer, outcome, feedback)
 * with the detailed follow-up reason preserved.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Select from '@radix-ui/react-select';
import { format, parseISO } from 'date-fns';
import { Check, ChevronDown, ChevronUp, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/design-system/primitives';
import { brand } from '@brand';
import { track } from '@/lib/analytics';
import {
  useAcceptCancelFlowDiscount,
  useCancelOrder,
  useConfirmCancelFlow,
  useOrders,
  usePauseSubscription,
  useReportCancelFlowOutcome,
  useStartCancelFlow,
  useSubmitCancelFlowFeedback,
} from '@/lib/api/hooks';
import type {
  CancelFlowReason,
  CancelFlowStart,
  MeSubscription,
} from '@/lib/api/hooks';
import { useSupportEmail } from '@/lib/supportEmail';
import { cancellationSummary } from '@/features/orders/OrderDetail';
import { useOrderWait, type OrderWait } from '@/features/orders/OrderProgress';

type ReasonCategory = 'too_expensive' | 'order_issue' | 'treatment_fit' | 'not_needed' | 'switched_provider' | 'other';
type ReasonOption = { value: CancelFlowReason; label: string };
const REASONS: { value: ReasonCategory; label: string; hint?: string; followUps?: ReasonOption[] }[] = [
  {
    value: 'order_issue', label: 'Something went wrong with my order',
    hint: 'Shipping, prescription, or support issue',
    followUps: [
      { value: 'never_arrived', label: 'Never arrived' },
      { value: 'arrived_late', label: 'Arrived late' },
      { value: 'arrived_hot_or_damaged', label: 'Arrived warm or damaged' },
      { value: 'prescription_issue', label: 'Prescription issue' },
      { value: 'communication_issue', label: 'Support didn’t help' },
    ],
  },
  {
    value: 'treatment_fit', label: 'The treatment isn’t right for me',
    hint: 'No results, side effects, or don’t like taking it',
    followUps: [
      { value: 'not_working', label: 'Haven’t seen results' },
      { value: 'side_effects', label: 'Side effects' },
      { value: 'dislike_treatment', label: 'Don’t like taking it' },
    ],
  },
  {
    value: 'not_needed', label: 'I don’t need it right now',
    hint: 'Reached my goal, have extra, or taking a break',
    followUps: [
      { value: 'reached_goal', label: 'Reached my goal' },
      { value: 'enough_supply', label: 'Have extra medication' },
      { value: 'taking_break', label: 'Taking a break' },
    ],
  },
  { value: 'switched_provider', label: 'I’m switching to another provider' },
  { value: 'too_expensive', label: 'It’s too expensive' },
  { value: 'other', label: 'Other' },
];

type Step =
  | 'reason'
  | 'confirm'
  | 'offer' // 25% discount (with or without the education preface)
  | 'pause-offer'
  | 'order-status'
  | 'priority-confirmed'
  | 'saved-discount'
  | 'saved-pause'
  | 'cancelled';

/**
 * The "results take time" paragraph, tailored to the medication being
 * cancelled (keyed off canonical product id + display name). Hedged
 * phrasing on purpose; falls back to a generic protocol note.
 */
function educationBody(sub: MeSubscription): string {
  const key = `${sub.product_id ?? ''} ${sub.product_name ?? ''}`.toLowerCase();
  if (/glp|tirzep|semaglut|gip/.test(key)) {
    return 'GLP-1 therapy builds gradually, and your dose steps up over the first months. Appetite changes often come first, with meaningful weight and body-composition changes typically building over months 2-3 and beyond. Stopping early is the most common reason patients never see the full effect.';
  }
  if (/serm/.test(key)) {
    return 'Sermorelin works with your body’s own rhythms, so it builds gradually. Deeper sleep tends to shift first, with recovery, energy, and body-composition changes typically building over months 2-3 and beyond. Stopping early is the most common reason patients never see the full effect.';
  }
  if (/nad/.test(key)) {
    return 'NAD+ levels rebuild gradually. Many patients notice steadier energy and sharper focus in the first few weeks, with the fuller effect on energy and recovery typically building over 2-3 months of consistent use. Stopping early is the most common reason patients never see the full effect.';
  }
  if (/gluta/.test(key)) {
    return 'Glutathione builds with consistent use. Many patients notice changes in skin tone and overall wellness over the first 2-3 months as levels build. Stopping early is the most common reason patients never see the full effect.';
  }
  if (/ghk/.test(key)) {
    return 'GHK-Cu works at the skin level, so changes are gradual. Visible shifts in skin texture and tone typically take 2-3 months of consistent use. Stopping early is the most common reason patients never see the full effect.';
  }
  if (/mic|b12/.test(key)) {
    return 'MIC+B12 supports energy and metabolism gradually. Many patients notice the difference build over the first 1-2 months of consistent weekly use. Stopping early is the most common reason patients never see the full effect.';
  }
  return 'These protocols build gradually. Many patients notice early shifts in the first few weeks, with the fuller effect typically building over 2-3 months of consistent use. Stopping early is the most common reason patients never see the full effect.';
}

function dollars(cents: number | null | undefined): string {
  if (cents == null) return '—';
  const v = cents / 100;
  return v % 1 === 0 ? `$${v}` : `$${v.toFixed(2)}`;
}

function dateLabel(iso: string | null | undefined): string | null {
  if (!iso) return null;
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return null;
  }
}

export function CancelFlowDialog({
  sub,
  open,
  onOpenChange,
}: {
  sub: MeSubscription;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const subId = sub.stripe_subscription_id;
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('reason');
  const [category, setCategory] = useState<ReasonCategory | null>(null);
  const [reason, setReason] = useState<CancelFlowReason | null>(null);
  const [reasonText, setReasonText] = useState('');
  const [flow, setFlow] = useState<CancelFlowStart | null>(null);
  const [pauseDays, setPauseDays] = useState<number | null>(null);
  const [endsAt, setEndsAt] = useState<string | null>(null);
  const [feedback, setFeedback] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const start = useStartCancelFlow();
  const acceptDiscount = useAcceptCancelFlowDiscount();
  const confirmCancel = useConfirmCancelFlow();
  const pause = usePauseSubscription();
  const reportOutcome = useReportCancelFlowOutcome();
  const submitFeedback = useSubmitCancelFlowFeedback();

  const busy =
    start.isPending ||
    acceptDiscount.isPending ||
    confirmCancel.isPending ||
    pause.isPending;

  // Storefront: each step replaces the last inside the dialog; focus moves
  // to the step's title (it fell back to the page body when the button
  // that led to the step unmounted). The first step keeps Radix's focus.
  const titleRef = useRef<HTMLHeadingElement>(null);
  const firstStep = useRef(true);
  useEffect(() => {
    if (!brand.storefrontAccount || !open) {
      firstStep.current = true;
      return;
    }
    if (firstStep.current) {
      firstStep.current = false;
      return;
    }
    titleRef.current?.focus();
  }, [step, open]);

  const productLabel = sub.product_name || 'your plan';
  const renewalLabel = useMemo(
    () => dateLabel(flow?.next_renewal_at ?? sub.next_renewal_at),
    [flow, sub.next_renewal_at],
  );

  function reset() {
    setStep('reason');
    setCategory(null);
    setReason(null);
    setReasonText('');
    setFlow(null);
    setPauseDays(null);
    setEndsAt(null);
    setFeedback('');
    setFeedbackSent(false);
    setError(null);
  }

  function close() {
    onOpenChange(false);
    reset();
  }

  const reasonValid = reason !== null;

  async function handleContinueFromReason() {
    if (!reason || !reasonValid) return;
    setError(null);
    track('cancel_flow_reason_selected', { sub_id: subId, reason });
    try {
      const res = await start.mutateAsync({
        subId,
        reason,
        reasonText: reason === 'other' || reason === 'switched_provider'
          ? reasonText.trim() || undefined
          : undefined,
      });
      setFlow(res);
      if (res.offer === 'pause') {
        setStep('pause-offer');
      } else if (res.offer === 'order_status') {
        setStep('order-status');
      } else if (res.offer === 'none') {
        setStep('confirm');
      } else if (res.discount_already_used) {
        // No second bite at the discount — go straight to the cancel
        // confirmation so the flow never dangles an offer we won't honor.
        setStep('confirm');
      } else {
        setStep('offer');
      }
    } catch (e) {
      setError(String((e as Error)?.message || e));
    }
  }

  async function doCancel(attemptId?: string) {
    const id = attemptId ?? flow?.attempt_id;
    if (!id) return;
    setError(null);
    try {
      const res = await confirmCancel.mutateAsync({ subId, attemptId: id });
      setEndsAt(res.next_renewal_at);
      track('cancel_flow_cancelled', { sub_id: subId, reason });
      setStep('cancelled');
    } catch (e) {
      setError(String((e as Error)?.message || e));
    }
  }

  async function handleAcceptDiscount() {
    if (!flow) return;
    setError(null);
    try {
      await acceptDiscount.mutateAsync({ subId, attemptId: flow.attempt_id });
      setStep('saved-discount');
    } catch (e) {
      setError(String((e as Error)?.message || e));
    }
  }

  async function handleAcceptPause(days: number) {
    if (!flow) return;
    setError(null);
    setPauseDays(days);
    try {
      await pause.mutateAsync({ subId, days });
      // Best-effort attempt bookkeeping — the pause itself already stuck.
      reportOutcome.mutate({
        subId,
        attemptId: flow.attempt_id,
        outcome: 'saved_pause',
        days,
      });
      setStep('saved-pause');
    } catch (e) {
      setError(String((e as Error)?.message || e));
    }
  }

  async function handleSendFeedback() {
    if (!flow || !feedback.trim()) return;
    try {
      await submitFeedback.mutateAsync({
        subId,
        attemptId: flow.attempt_id,
        text: feedback.trim(),
      });
      setFeedbackSent(true);
    } catch {
      // Feedback is best-effort — don't trap the patient on the way out.
      setFeedbackSent(true);
    }
  }

  const declineToCancel = (
    <Button
      size="sm"
      variant="ghost"
      disabled={busy}
      onClick={() => {
        track('cancel_flow_offer_declined', {
          sub_id: subId,
          reason,
          offer: flow?.offer,
        });
        // Storefront: through the confirm step, which says what happens
        // to an order that is still waiting for its prescription.
        if (brand.pendingOrderCancelNotice) setStep('confirm');
        else void doCancel();
      }}
    >
      No thanks, continue to cancel
    </Button>
  );

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) {
          track('cancel_flow_closed', { sub_id: subId, reason, step });
          close();
        } else {
          onOpenChange(o);
        }
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/10 backdrop-blur-[10px] z-dialog" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2
                     w-[min(44rem,calc(100vw-20px))] max-h-[90vh] overflow-hidden
                     bg-[var(--surface-page)] border border-line-subtle z-dialog flex flex-col"
          aria-describedby={undefined}
        >
          <header className="flex items-center justify-between bg-[var(--surface-sunken)] border-b border-line-subtle pr-2">
            <Dialog.Title
              ref={titleRef}
              tabIndex={brand.storefrontAccount ? -1 : undefined}
              className={`font-mono uppercase text-13 text-ink-secondary px-15 py-15${brand.storefrontAccount ? ' outline-none' : ''}`}
            >
              {step === 'cancelled'
                ? 'SUBSCRIPTION CANCELLED'
                : step === 'saved-discount' ||
                    step === 'saved-pause' ||
                    step === 'priority-confirmed'
                  ? 'YOU’RE ALL SET'
                  : brand.storefrontAccount && sub.product_name
                    ? `Cancel ${sub.product_name}`
                    : 'CANCEL SUBSCRIPTION'}
            </Dialog.Title>
            <Dialog.Close
              aria-label="Close"
              className="w-45 h-45 flex items-center justify-center border-l border-line-subtle bg-slate text-ink-secondary hover:text-ink"
            >
              <X size={18} strokeWidth={1.5} />
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto px-15 py-15">
            {step === 'reason' && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">
                  Before you go, what’s the main reason?
                </p>
                <div className="flex flex-col gap-8" role="radiogroup" aria-label="Main cancellation reason">
                  {REASONS.map((r) => {
                    const checked = category === r.value;
                    return (
                      <div key={r.value} className={`border ${checked ? 'border-ink bg-[var(--surface-sunken)]' : 'border-line-default'}`}>
                        <label className="flex items-center gap-12 px-12 py-10 cursor-pointer type-body-sm hover:bg-[var(--surface-sunken)]">
                          <input
                            type="radio"
                            name="cancel-reason"
                            checked={checked}
                            aria-label={r.label}
                            onChange={() => {
                              setCategory(r.value);
                              setReason(r.followUps ? null : r.value as CancelFlowReason);
                              setReasonText('');
                              setError(null);
                            }}
                            className="w-18 h-18 shrink-0 accent-[var(--ink-primary)]"
                          />
                          <span className="flex flex-col gap-4">
                            <span>{r.label}</span>
                            {r.hint && <span className="text-ink-muted type-body-sm">{r.hint}</span>}
                          </span>
                        </label>
                        {checked && r.followUps && (
                          <div className="px-12 pb-12 flex flex-col gap-8">
                            <label htmlFor="cancel-follow-up" className="type-body-sm">What happened?</label>
                            <Select.Root required value={reason ?? ''} onValueChange={(value) => setReason(value as CancelFlowReason)}>
                              <Select.Trigger
                                id="cancel-follow-up"
                                className="flex min-h-44 w-full items-center justify-between gap-12 border border-ink bg-page px-12 py-10 text-left type-body-sm text-ink outline-none focus-visible:ring-2 focus-visible:ring-ink focus-visible:ring-offset-2 data-[placeholder]:text-ink-muted"
                              >
                                <Select.Value placeholder="Select a reason" />
                                <Select.Icon asChild><ChevronDown className="h-16 w-16 shrink-0" /></Select.Icon>
                              </Select.Trigger>
                              <Select.Portal>
                                <Select.Content
                                  position="popper"
                                  sideOffset={4}
                                  collisionPadding={12}
                                  className="z-popover max-h-[var(--radix-select-content-available-height)] w-[var(--radix-select-trigger-width)] overflow-hidden border border-ink bg-page text-ink shadow-dropdown"
                                >
                                  <Select.ScrollUpButton className="flex justify-center py-4"><ChevronUp className="h-16 w-16" /></Select.ScrollUpButton>
                                  <Select.Viewport>
                                    {r.followUps.map((option) => (
                                      <Select.Item
                                        key={option.value}
                                        value={option.value}
                                        className="relative flex min-h-44 cursor-pointer items-center border-b border-line-subtle py-12 pl-12 pr-36 type-body-sm outline-none last:border-b-0 data-[highlighted]:bg-ink data-[highlighted]:text-page data-[state=checked]:font-medium"
                                      >
                                        <Select.ItemText>{option.label}</Select.ItemText>
                                        <Select.ItemIndicator className="absolute right-12"><Check className="h-16 w-16" /></Select.ItemIndicator>
                                      </Select.Item>
                                    ))}
                                  </Select.Viewport>
                                  <Select.ScrollDownButton className="flex justify-center py-4"><ChevronDown className="h-16 w-16" /></Select.ScrollDownButton>
                                </Select.Content>
                              </Select.Portal>
                            </Select.Root>
                          </div>
                        )}
                        {checked && r.value === 'switched_provider' && (
                          <div className="px-12 pb-12 flex flex-col gap-8">
                            <label htmlFor="cancel-provider" className="type-body-sm">Who to? (optional)</label>
                            <input
                              id="cancel-provider"
                              type="text"
                              value={reasonText}
                              maxLength={2000}
                              onChange={(e) => setReasonText(e.target.value)}
                              placeholder="Provider name"
                              className="border border-line-default bg-[var(--surface-page)] px-10 py-8 type-body-sm w-full"
                            />
                          </div>
                        )}
                        {checked && r.value === 'other' && (
                          <div className="px-12 pb-12 flex flex-col gap-8">
                            <label htmlFor="cancel-reason-details" className="type-body-sm">Tell us more (optional)</label>
                            <textarea
                              id="cancel-reason-details"
                              value={reasonText}
                              onChange={(e) => setReasonText(e.target.value)}
                              placeholder="Please describe why you’re cancelling"
                              rows={3}
                              maxLength={2000}
                              className="border border-line-default bg-[var(--surface-page)] px-10 py-8 type-body-sm w-full"
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-8">
                  <Button
                    size="sm"
                    disabled={!reasonValid}
                    loading={start.isPending || confirmCancel.isPending}
                    onClick={() => void handleContinueFromReason()}
                  >
                    Continue
                  </Button>
                  <Button size="sm" variant="ghost" onClick={close}>
                    Keep my subscription
                  </Button>
                </div>
              </div>
            )}

            {step === 'confirm' && flow && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">Confirm cancellation</p>
                <p className="type-body-sm text-ink-muted">
                  Your plan will stay active through the end of your current
                  paid period. Future renewals will stop.
                </p>
                {brand.pendingOrderCancelNotice && <PendingOrderNotice sub={sub} />}
                <div className="flex flex-wrap gap-8">
                  <Button
                    size="sm"
                    disabled={busy}
                    loading={confirmCancel.isPending}
                    onClick={() => void doCancel()}
                  >
                    Confirm cancellation
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={close}
                  >
                    Keep my subscription
                  </Button>
                </div>
              </div>
            )}

            {step === 'offer' && flow && (
              <div className="flex flex-col gap-12">
                {reason === 'not_working' && (
                  <>
                    <p className="type-title-md">
                      Results take time. Most patients see changes by month
                      2-3
                    </p>
                    <p className="type-body-sm text-ink-muted">
                      {educationBody(sub)}
                    </p>
                    <p className="type-body-sm">
                      Keep going with{' '}
                      <strong>{flow.discount_percent ?? 25}% off</strong> your
                      next renewal:
                    </p>
                  </>
                )}
                {category === 'order_issue' && (
                  <p className="type-body-sm text-ink-muted">
                    We’re sorry something went wrong with your order.
                  </p>
                )}
                {reason !== 'not_working' && (
                  <>
                    <p className="type-title-md">
                      How about {flow.discount_percent ?? 25}% off your next
                      renewal?
                    </p>
                    <p className="type-body-sm text-ink-muted">
                      We’d rather help you keep going than see you go. Take{' '}
                      {flow.discount_percent ?? 25}% off your next renewal,
                      applied automatically. No code needed.
                    </p>
                  </>
                )}
                <div className="bg-surface-subtle px-12 py-10 type-body-sm flex flex-col gap-4">
                  <div className="flex justify-between">
                    <span>Next renewal{renewalLabel ? ` · ${renewalLabel}` : ''}</span>
                    <span>
                      <s className="text-ink-muted mr-8">
                        {dollars(flow.renewal_price_cents)}
                      </s>
                      <strong>{dollars(flow.discounted_price_cents)}</strong>
                    </span>
                  </div>
                  {flow.renewal_price_cents != null &&
                    flow.discounted_price_cents != null && (
                      <span className="type-label-sm text-ink-muted uppercase">
                        Special offer: get{' '}
                        {dollars(
                          flow.renewal_price_cents -
                            flow.discounted_price_cents,
                        )}{' '}
                        off your next renewal
                      </span>
                    )}
                </div>
                <div className="flex flex-wrap gap-8">
                  <Button
                    size="sm"
                    loading={acceptDiscount.isPending}
                    disabled={busy}
                    onClick={() => void handleAcceptDiscount()}
                  >
                    {reason === 'not_working'
                      ? `Keep trying with ${flow.discount_percent ?? 25}% off`
                      : `Take ${flow.discount_percent ?? 25}% off and stay`}
                  </Button>
                  {declineToCancel}
                </div>
              </div>
            )}

            {step === 'pause-offer' && flow && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">
                  {/* A storefront plan bills monthly or once per 3, 6 or 12
                      months, so a 1- or 2-month pause moves its next bill
                      instead of skipping one. */}
                  {brand.cycleDayPlanLabels
                    ? 'Pause instead? Keep your plan, skip the next bill'
                    : 'Pause instead? Keep your plan and move your next bill back'}
                </p>
                <p className="type-body-sm text-ink-muted">
                  {reason === 'reached_goal'
                    ? 'If you’ve reached your goal, you can pause your plan instead of cancelling.'
                    : reason === 'taking_break'
                    ? 'If you’re taking a break, pausing just pushes your next renewal out.'
                    : 'Since you’re stocked up, pausing just pushes your next renewal out.'} Nothing bills or ships until then, and you can
                  resume anytime from your account.
                </p>
                <div className="flex flex-wrap gap-8">
                  {(flow.pause_options_days.length
                    ? flow.pause_options_days
                    : [30, 60]
                  ).map((days) => (
                    <Button
                      key={days}
                      size="sm"
                      loading={pause.isPending && pauseDays === days}
                      disabled={busy}
                      onClick={() => void handleAcceptPause(days)}
                    >
                      Pause {days === 30 ? '1 month' : days === 60 ? '2 months' : `${days} days`}
                    </Button>
                  ))}
                  {declineToCancel}
                </div>
              </div>
            )}

            {step === 'order-status' && flow && (
              <OrderStatusStep
                flow={flow}
                busy={busy}
                cancelPending={confirmCancel.isPending}
                onKeepOrder={(cta) => {
                  track('cancel_flow_order_kept', {
                    sub_id: subId,
                    order_state: flow.order_context?.state,
                    cta,
                  });
                  if (cta === 'priority') {
                    // Don't just close on them: confirm the 24-hour
                    // priority-review promise on its own screen.
                    setStep('priority-confirmed');
                    return;
                  }
                  if (cta === 'intake') navigate('/app/intake');
                  else if (cta === 'consult')
                    navigate('/app/clinic?tab=appointments&book=1');
                  else if (
                    cta === 'tracking' &&
                    flow.order_context?.tracking_url
                  )
                    window.open(flow.order_context.tracking_url, '_blank');
                  if (cta !== 'tracking') close();
                }}
                onCancelAnyway={() => {
                  track('cancel_flow_offer_declined', {
                    sub_id: subId,
                    reason,
                    offer: 'order_status',
                    order_state: flow.order_context?.state,
                  });
                  void doCancel();
                }}
              />
            )}

            {step === 'priority-confirmed' && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">
                  You’re in priority review
                </p>
                <p className="type-body-sm text-ink-muted">
                  Your order has been pushed to the front of the queue. Your
                  provider will review it and write your prescription within
                  the next 24 hours, and your order ships right after with
                  tracking. Thanks for sticking with us.
                </p>
                <div>
                  <Button size="sm" onClick={close}>
                    Done
                  </Button>
                </div>
              </div>
            )}

            {step === 'saved-discount' && flow && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">
                  You’re all set: {flow.discount_percent ?? 25}% off applied
                </p>
                <p className="type-body-sm text-ink-muted">
                  Your {productLabel} plan continues as usual
                  {renewalLabel ? (
                    <>
                      {' '}
                      and your next renewal on <strong>{renewalLabel}</strong>{' '}
                      bills at{' '}
                      <strong>{dollars(flow.discounted_price_cents)}</strong>{' '}
                      instead of {dollars(flow.renewal_price_cents)}.
                    </>
                  ) : (
                    ' with the discount applied to your next renewal.'
                  )}
                </p>
                <div>
                  <Button size="sm" onClick={close}>
                    Done
                  </Button>
                </div>
              </div>
            )}

            {step === 'saved-pause' && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">Paused. See you soon</p>
                <p className="type-body-sm text-ink-muted">
                  Nothing bills or ships for the next{' '}
                  {pauseDays === 30
                    ? 'month'
                    : pauseDays === 60
                      ? '2 months'
                      : `${pauseDays} days`}
                  . You can resume anytime from your account, and we’ll pick
                  up right where you left off.
                </p>
                <div>
                  <Button size="sm" onClick={close}>
                    Done
                  </Button>
                </div>
              </div>
            )}

            {step === 'cancelled' && (
              <div className="flex flex-col gap-12">
                <p className="type-title-md">
                  Your plan is cancelled. We’re sorry to see you go
                </p>
                <p className="type-body-sm text-ink-muted">
                  {productLabel.charAt(0).toUpperCase() + productLabel.slice(1)}{' '}
                  stays active
                  {endsAt && dateLabel(endsAt) ? (
                    <>
                      {' '}
                      through <strong>{dateLabel(endsAt)}</strong>. You keep
                      everything you’ve already paid for, and nothing bills
                      after that.
                    </>
                  ) : (
                    ' through the end of your current period. You keep everything you’ve already paid for, and nothing bills after that.'
                  )}{' '}
                  You can undo this from your account anytime before then.
                </p>
                {!feedbackSent ? (
                  <div className="flex flex-col gap-8">
                    <label className="type-label-sm uppercase tracking-tight text-ink-muted">
                      Anything we could have done better?
                    </label>
                    <textarea
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      rows={3}
                      maxLength={4000}
                      placeholder="Optional. We read every one"
                      className="border border-line-default px-10 py-8 type-body-sm w-full"
                    />
                    <div className="flex gap-8">
                      <Button
                        size="sm"
                        disabled={!feedback.trim()}
                        loading={submitFeedback.isPending}
                        onClick={() => void handleSendFeedback()}
                      >
                        Send
                      </Button>
                      <Button size="sm" variant="ghost" onClick={close}>
                        Close
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-8">
                    <p className="type-body-sm">
                      Thank you. We read every one.
                    </p>
                    <div>
                      <Button size="sm" onClick={close}>
                        Close
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <p className="mt-12 type-body-sm text-status-danger">{error}</p>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const PRE_RX_STATUSES = new Set(['awaiting_prescription', 'pending_review', 'pending_approval']);
const AT_PHARMACY_STATUSES = new Set(['awaiting_pharmacy_routing', 'processing']);

/**
 * Storefront cancel flow: what happens to this plan's order that has not
 * shipped yet. Before a prescription, it stays in review and ships if
 * prescribed; a provider decline cancels the plan and refunds the order
 * (the backend's clinician-reject workflow). It can be cancelled here when
 * the backend allows it (`cancellable`), else by email to support.
 */
function PendingOrderNotice({ sub }: { sub: MeSubscription }) {
  const { data: orders } = useOrders();
  const supportEmail = useSupportEmail();
  const cancelOrder = useCancelOrder();
  const [confirming, setConfirming] = useState(false);
  const order = (orders ?? []).find(
    (o) =>
      o.stripe_subscription_id === sub.stripe_subscription_id &&
      (PRE_RX_STATUSES.has(o.status) || AT_PHARMACY_STATUSES.has(o.status)),
  );
  // The step it waits on, as Home, Orders and Treatments name it.
  const { wait } = useOrderWait(order);
  if (!order) return null;
  const product = order.lines[0]?.name ?? sub.product_name ?? 'your';
  const placed = dateLabel(order.created_at);
  const preRx = PRE_RX_STATUSES.has(order.status);

  return (
    <section
      aria-label="Your order that has not shipped"
      className="border border-line-subtle bg-sunken px-12 py-12 flex flex-col gap-8"
    >
      <p className="type-body-sm text-ink">
        Your <strong className="font-medium">{product}</strong> order
        {placed ? ` from ${placed}` : ''}
        {preRx ? ` ${pendingOrderWaitPhrase(wait)}` : ' is already with the pharmacy.'}
      </p>
      {preRx ? (
        <ul className="list-disc pl-18 flex flex-col gap-4 type-body-sm text-ink-secondary">
          <li>Cancelling your plan does not cancel this order. It stays open and ships if it is prescribed.</li>
          <li>If the provider decides this treatment is not right for you, the plan ends and this order is refunded.</li>
          {!order.cancellable && (
            <li>
              To cancel this order as well, email{' '}
              <a
                className="underline underline-offset-2 [overflow-wrap:anywhere]"
                href={`mailto:${supportEmail}?subject=${encodeURIComponent(`Cancel my order ${order.order_number}`)}`}
              >
                {supportEmail}
              </a>{' '}
              before it is prescribed.
            </li>
          )}
        </ul>
      ) : (
        <p className="type-body-sm text-ink-secondary">
          It has been prescribed and will still ship. Cancelling stops the renewals after it.
        </p>
      )}
      {order.cancellable && preRx && !cancelOrder.data && (
        confirming ? (
          <div className="flex flex-wrap items-center gap-8">
            <span className="type-body-sm text-ink">Cancel this order and the plan now?</span>
            <Button
              size="sm"
              variant="danger"
              loading={cancelOrder.isPending}
              onClick={() => cancelOrder.mutate(order.id)}
            >
              Yes, cancel the order
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirming(false)}>
              Keep it
            </Button>
          </div>
        ) : (
          <div>
            <Button size="sm" variant="secondary" onClick={() => setConfirming(true)}>
              Cancel this order too
            </Button>
            <p className="type-body-sm text-ink-muted mt-6">
              For a refund of a cancelled order, email {supportEmail}.
            </p>
          </div>
        )
      )}
      {cancelOrder.data && (
        <p role="status" className="type-body-sm text-ink">
          {cancellationSummary(cancelOrder.data)}
        </p>
      )}
      {cancelOrder.error && (
        <p role="alert" className="type-body-sm text-danger">
          {(cancelOrder.error as { message?: string }).message ?? 'The order could not be cancelled.'}
        </p>
      )}
    </section>
  );
}

/** "Your Sermorelin order from Oct 22 …" + what it is waiting on. */
function pendingOrderWaitPhrase(wait: OrderWait): string {
  switch (wait) {
    case 'intake':
      return 'is waiting on a few intake questions.';
    case 'id':
      return 'is waiting on your photo ID before a provider can review it.';
    case 'visit':
      return 'is waiting on a visit with a provider.';
    case 'visit_booked':
      return 'is waiting on your booked visit with a provider.';
    default:
      return 'is with a provider for review.';
  }
}

function OrderStatusStep({
  flow,
  busy,
  cancelPending,
  onKeepOrder,
  onCancelAnyway,
}: {
  flow: CancelFlowStart;
  busy: boolean;
  cancelPending: boolean;
  onKeepOrder: (cta: 'intake' | 'consult' | 'priority' | 'tracking' | 'close') => void;
  onCancelAnyway: () => void;
}) {
  const ctx = flow.order_context;
  const state = ctx?.state ?? 'unknown';
  const shippedLabel = dateLabel(ctx?.shipped_at);
  const deliveredLabel = dateLabel(ctx?.delivered_at);

  const cancelBtn = (
    <Button size="sm" variant="ghost" disabled={busy} onClick={onCancelAnyway}>
      {cancelPending ? 'Cancelling…' : 'No thanks, continue to cancel'}
    </Button>
  );

  if (state === 'intake_required') {
    return (
      <div className="flex flex-col gap-12">
        <p className="type-title-md">Your order is waiting on your intake</p>
        <p className="type-body-sm text-ink-muted">
          We’re sorry for the confusion. Your order can’t move to the
          pharmacy until your clinical intake is complete. It takes about 2
          minutes, and then your provider can write your prescription and get
          it shipped.
        </p>
        <div className="flex flex-wrap gap-8">
          <Button size="sm" disabled={busy} onClick={() => onKeepOrder('intake')}>
            Complete my intake
          </Button>
          {cancelBtn}
        </div>
      </div>
    );
  }
  if (state === 'consult_required') {
    return (
      <div className="flex flex-col gap-12">
        <p className="type-title-md">
          Your order is waiting on a quick provider visit
        </p>
        <p className="type-body-sm text-ink-muted">
          Your state requires a short visit before your provider can
          prescribe. Book it and your order moves forward right after. We’re
          sorry it wasn’t clearer that this was the holdup.
        </p>
        <div className="flex flex-wrap gap-8">
          <Button size="sm" disabled={busy} onClick={() => onKeepOrder('consult')}>
            Schedule my visit
          </Button>
          {cancelBtn}
        </div>
      </div>
    );
  }
  if (state === 'awaiting_prescription') {
    return (
      <div className="flex flex-col gap-12">
        <p className="type-title-md">
          We’re sorry, this delay is on us, not you
        </p>
        <p className="type-body-sm text-ink-muted">
          Your intake is done and your order is in our provider’s queue.
          We’ve flagged it for <strong>priority review</strong>: your
          provider typically writes the prescription within 24 hours, and
          your order ships right after with tracking.
        </p>
        <div className="flex flex-wrap gap-8">
          <Button size="sm" disabled={busy} onClick={() => onKeepOrder('priority')}>
            Keep my order (priority review)
          </Button>
          {cancelBtn}
        </div>
      </div>
    );
  }
  if (state === 'processing') {
    return (
      <div className="flex flex-col gap-12">
        <p className="type-title-md">It’s at the pharmacy now</p>
        <p className="type-body-sm text-ink-muted">
          Your prescription is written and the pharmacy is preparing your
          order{ctx?.order_number ? ` (${ctx.order_number})` : ''}. You’ll
          get tracking the moment it ships. Sorry it’s taken longer than it
          should.
        </p>
        <div className="flex flex-wrap gap-8">
          <Button size="sm" disabled={busy} onClick={() => onKeepOrder('close')}>
            Got it, I’ll wait for tracking
          </Button>
          {cancelBtn}
        </div>
      </div>
    );
  }
  if (state === 'shipped') {
    return (
      <div className="flex flex-col gap-12">
        <p className="type-title-md">Good news: it’s on the way</p>
        <p className="type-body-sm text-ink-muted">
          Your order shipped{shippedLabel ? ` on ${shippedLabel}` : ''} and
          should arrive within about 2 days.
          {ctx?.tracking_number ? ` Tracking: ${ctx.tracking_number}.` : ''}
        </p>
        <div className="flex flex-wrap gap-8">
          {ctx?.tracking_url ? (
            <Button size="sm" disabled={busy} onClick={() => onKeepOrder('tracking')}>
              Track my order
            </Button>
          ) : (
            <Button size="sm" disabled={busy} onClick={() => onKeepOrder('close')}>
              Got it
            </Button>
          )}
          {cancelBtn}
        </div>
      </div>
    );
  }
  if (state === 'delivered') {
    return (
      <div className="flex flex-col gap-12">
        <p className="type-title-md">Our records show it was delivered</p>
        <p className="type-body-sm text-ink-muted">
          This order was marked delivered
          {deliveredLabel ? ` on ${deliveredLabel}` : ''}. If it never made
          it to you, message support from your home screen and we’ll make it
          right.
        </p>
        <div className="flex flex-wrap gap-8">
          <Button size="sm" disabled={busy} onClick={() => onKeepOrder('close')}>
            Got it
          </Button>
          {cancelBtn}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-12">
      <p className="type-title-md">We’re sorry your order didn’t make it</p>
      <p className="type-body-sm text-ink-muted">
        That’s not the experience we want. Message support from your home
        screen and we’ll track it down and make it right, or continue below.
      </p>
      <div className="flex flex-wrap gap-8">
        <Button size="sm" disabled={busy} onClick={() => onKeepOrder('close')}>
          Got it
        </Button>
        {cancelBtn}
      </div>
    </div>
  );
}
